import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantContext} from '@hconnect/upm'
import {useQuery} from '@tanstack/react-query'

import {AutofillStatus, type AutofillStatusData, type AutofillStatusValue} from '../consts'

const AUTOFILL_REFETCH_INTERVAL = 5 * 60 * 1000
const AUTOFILL_REFETCH_INTERVAL_IF_RUNNING = 30 * 1000

const AUTOFILL_STATUS_QUERY_KEY = 'autofillStatus'

type AutofillStatusApiResponse = {
  status: AutofillStatusValue
  last_updated_at: string | null
}

const fetchAutofillStatus = async (
  plantCode: string,
  signal?: AbortSignal
): Promise<AutofillStatusData> => {
  const response = await ApiClient.axiosInstance.get<AutofillStatusApiResponse>(
    `/hcem/ui/v2/autofill/${plantCode}/status`,
    {signal}
  )
  return {
    status: response.data.status,
    lastUpdatedAt: response.data.last_updated_at
  }
}

export const useGetAutofillStatus = () => {
  // this logic is so refresh period is AUTOFILL_REFETCH_INTERVAL_IF_RUNNING ( 30s )
  // when autofill is running, otherwise it is AUTOFILL_REFETCH_INTERVAL ( 5min )

  const {plantId: plantCode} = useUpmPlantContext()

  const autofillStatusOverview = useQuery<AutofillStatusData>({
    queryKey: [AUTOFILL_STATUS_QUERY_KEY, plantCode],
    queryFn: ({signal}) => fetchAutofillStatus(plantCode, signal),
    throwOnError: true,
    refetchInterval: AUTOFILL_REFETCH_INTERVAL,
    refetchOnMount: false
  })

  const isRunning = autofillStatusOverview.data?.status === AutofillStatus.InProgress

  const autofillStatusIfRunning = useQuery<AutofillStatusData>({
    queryKey: [AUTOFILL_STATUS_QUERY_KEY, plantCode],
    queryFn: ({signal}) => fetchAutofillStatus(plantCode, signal),
    throwOnError: true,
    refetchInterval: AUTOFILL_REFETCH_INTERVAL_IF_RUNNING,
    refetchOnMount: false,
    enabled: isRunning,
    // preventing disabled queries from firing during invalidation
    ...(isRunning ? {} : {gcTime: 0})
  })

  if (!isRunning) {
    return autofillStatusOverview
  }

  return autofillStatusIfRunning
}
